import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Empty, Input, Popconfirm, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { getApiError } from '../../api/client';
import type { Client } from '../../api/types';
import { ActiveSubscriptionBadge } from '../../components/ActiveSubscriptionBadge';
import { CardRow, MobileCardList } from '../../components/MobileCardList';
import { useIsMobile } from '../../lib/responsive';
import { usePersistedState } from '../../lib/usePersistedState';
import { useFirmaCurenta } from '../firme/FirmaCurentaContext';
import { deleteClient, fetchClienti } from './api';
import { ClientFormDrawer } from './ClientFormDrawer';
import { ClientImportModal } from './ClientImportModal';

const { Title } = Typography;
const PAGE_SIZE = 20;

export function ClientiListPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { firmaId } = useFirmaCurenta();
  const isMobile = useIsMobile();
  const [page, setPage] = usePersistedState('clienti.page', 1);
  const [search, setSearch] = usePersistedState('clienti.search', '');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['clients', firmaId, { page, pageSize: PAGE_SIZE, search }],
    queryFn: () => fetchClienti({ companyId: firmaId as string, page, pageSize: PAGE_SIZE, search: search || undefined }),
    enabled: !!firmaId,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteClient,
    onSuccess: () => {
      message.success('Clientul a fost șters.');
      queryClient.invalidateQueries({ queryKey: ['clients', firmaId] });
    },
    onError: (error) => {
      const apiError = getApiError(error);
      message.error(apiError?.message ?? 'Clientul nu a putut fi șters.');
    },
  });

  function openCreate() {
    setEditingId(null);
    setDrawerOpen(true);
  }
  function openEdit(id: string) {
    setEditingId(id);
    setDrawerOpen(true);
  }
  function closeDrawer() {
    setDrawerOpen(false);
    setEditingId(null);
  }

  const columns: ColumnsType<Client> = [
    {
      title: 'Denumire',
      dataIndex: 'name',
      render: (_, row) => (
        <>
          {row.name}
          <ActiveSubscriptionBadge active={row.hasActiveSubscription} />
        </>
      ),
    },
    {
      title: 'CUI/CNP',
      key: 'ident',
      render: (_, row) => (row.kind === 'COMPANY' ? (row.cui ?? '—') : (row.cnp ?? '—')),
    },
    { title: 'Localitate', dataIndex: 'cityName' },
    { title: 'Județ', dataIndex: 'countyCode' },
    {
      title: 'Tip',
      dataIndex: 'kind',
      render: (v: string) => <Tag color={v === 'COMPANY' ? 'blue' : 'purple'}>{v === 'COMPANY' ? 'PJ' : 'PF'}</Tag>,
    },
    {
      title: 'Stare',
      dataIndex: 'isActive',
      render: (v: boolean) => (v ? <Tag color="green">Activ</Tag> : <Tag>Inactiv</Tag>),
    },
    {
      title: 'Acțiuni',
      key: 'actions',
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row.id)}>
            Editează
          </Button>
          <Popconfirm
            title="Ștergi clientul?"
            description="Clientul poate fi șters doar dacă nu are facturi sau abonamente asociate."
            onConfirm={() => deleteMutation.mutate(row.id)}
          >
            <Button size="small" danger loading={deleteMutation.isPending && deleteMutation.variables === row.id}>
              Șterge
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={3} style={{ margin: '0 0 16px' }}>
        Clienți
      </Title>
      {!firmaId ? (
        <Empty description="Selectează sau adaugă mai întâi o firmă." />
      ) : (
        <>
          <div
            style={{
              marginBottom: 16,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <Input.Search
              placeholder="Caută după denumire sau CUI"
              allowClear
              defaultValue={search}
              style={{ width: isMobile ? '100%' : 320 }}
              onSearch={(value) => {
                setSearch(value);
                setPage(1);
              }}
            />
            <div style={{ display: 'flex', gap: 8, width: isMobile ? '100%' : undefined }}>
              <Button
                icon={<UploadOutlined />}
                block={isMobile}
                style={isMobile ? { flex: 1 } : undefined}
                onClick={() => setImportOpen(true)}
              >
                Importă clienți
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                block={isMobile}
                style={isMobile ? { flex: 1 } : undefined}
                onClick={openCreate}
              >
                Adaugă client
              </Button>
            </div>
          </div>
          {isMobile ? (
            <MobileCardList<Client>
              dataSource={data?.items}
              rowKey={(row) => row.id}
              loading={isLoading}
              emptyText="Niciun client."
              pagination={{ current: page, pageSize: PAGE_SIZE, total: data?.total ?? 0, onChange: setPage }}
              renderCard={(row) => (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                    <Typography.Text strong style={{ fontSize: 15 }}>
                      {row.name}
                      <ActiveSubscriptionBadge active={row.hasActiveSubscription} />
                    </Typography.Text>
                    <Space size={4} style={{ flex: '0 0 auto' }}>
                      <Tag color={row.kind === 'COMPANY' ? 'blue' : 'purple'} style={{ marginInlineEnd: 0 }}>
                        {row.kind === 'COMPANY' ? 'PJ' : 'PF'}
                      </Tag>
                      {row.isActive ? (
                        <Tag color="green" style={{ marginInlineEnd: 0 }}>Activ</Tag>
                      ) : (
                        <Tag style={{ marginInlineEnd: 0 }}>Inactiv</Tag>
                      )}
                    </Space>
                  </div>
                  <CardRow label={row.kind === 'COMPANY' ? 'CUI' : 'CNP'}>
                    {row.kind === 'COMPANY' ? (row.cui ?? '—') : (row.cnp ?? '—')}
                  </CardRow>
                  <CardRow label="Localitate">
                    {row.cityName}
                    {row.countyCode ? `, ${row.countyCode}` : ''}
                  </CardRow>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <Button size="small" block onClick={() => openEdit(row.id)}>
                      Editează
                    </Button>
                    <Popconfirm
                      title="Ștergi clientul?"
                      description="Clientul poate fi șters doar dacă nu are facturi sau abonamente asociate."
                      onConfirm={() => deleteMutation.mutate(row.id)}
                    >
                      <Button size="small" danger block loading={deleteMutation.isPending && deleteMutation.variables === row.id}>
                        Șterge
                      </Button>
                    </Popconfirm>
                  </div>
                </>
              )}
            />
          ) : (
            <Table<Client>
              rowKey="id"
              columns={columns}
              dataSource={data?.items}
              loading={isLoading}
              scroll={{ x: 'max-content' }}
              pagination={{
                current: page,
                pageSize: PAGE_SIZE,
                total: data?.total ?? 0,
                onChange: setPage,
                showSizeChanger: false,
              }}
            />
          )}
          <ClientFormDrawer open={drawerOpen} firmaId={firmaId} editingId={editingId} onClose={closeDrawer} />
          <ClientImportModal
            open={importOpen}
            firmaId={firmaId}
            onClose={() => setImportOpen(false)}
            onDone={() => queryClient.invalidateQueries({ queryKey: ['clients', firmaId] })}
          />
        </>
      )}
    </div>
  );
}
